import { useCallback, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import type { RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { ErrorModal } from "@dexteel/mesf-core";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import { ColumnGroupTintStyles } from "@/controls/ag-grid/ColumnGroupTints";
import {
	HEADER_PAD_CLASS,
	ONE_LINE_CELL_CLASS,
	OneLineCellStyles,
} from "@/controls/ag-grid/OneLineCells";
import { LIST_ROW_HEIGHT } from "@/controls/ag-grid/listGrid";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import type { RecipeStand } from "../models/Recipe";
import { useRecipeStands } from "../hooks/useRecipes";
import { useUpsertRecipeStand } from "../hooks/useRecipeMutation";
import { useStandsTableData } from "../hooks/useStandsTableData";
import { RecipeStandFormModal } from "./RecipeStandFormModal";

interface RecipeStandsTableProps {
	recipeId: number;
}

export const RecipeStandsTable = ({ recipeId }: RecipeStandsTableProps) => {
	const gridRef = useRef<AgGridReact<RecipeStand>>(null);
	const [editingStand, setEditingStand] = useState<RecipeStand | null>(null);
	const [formModalOpen, setFormModalOpen] = useState(false);
	const [notification, setNotification] = useState("");
	const [error, setError] = useState("");

	const { stands, isLoading, error: queryError } = useRecipeStands(recipeId);

	// Only rolling stands belong in this editor. Older recipes may retain shear
	// rows; straighteners are configured on their own screens.
	const millStands = useMemo(
		() =>
			stands.filter(
				(stand) =>
					!["STRAIGHTENER", "SHEAR", "COOLING_BED"].includes(
						stand.StandType ?? "",
					),
			),
		[stands],
	);
	const { columnDefs } = useStandsTableData();

	// One line per stand, so the whole mill fits the tab without scrolling.
	const defaultColDef = useMemo(
		() => ({
			...globalDefaultColDef,
			wrapText: false,
			autoHeight: false,
			cellClass: ONE_LINE_CELL_CLASS,
			headerClass: HEADER_PAD_CLASS,
		}),
		[],
	);

	const { upsertRecipeStand, isUpserting } = useUpsertRecipeStand({
		onSuccess: () => {
			setNotification("Stand updated successfully");
			setFormModalOpen(false);
			setEditingStand(null);
		},
		onError: (err) => setError(err.message),
	});

	const onRowClicked = useCallback((event: RowClickedEvent<RecipeStand>) => {
		if (event.data) {
			setEditingStand(event.data);
			setFormModalOpen(true);
		}
	}, []);

	const getRowId = useCallback((params: { data: RecipeStand }) => {
		return `${params.data.RecipeId}-${params.data.StandId}`;
	}, []);

	const handleSave = useCallback(
		(stand: RecipeStand) => {
			upsertRecipeStand({ recipeId, stand });
		},
		[recipeId, upsertRecipeStand],
	);

	return (
		<Box
			sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
		>
			<OneLineCellStyles />
			<ColumnGroupTintStyles />

			{queryError && (
				<Typography color="error" variant="body2" sx={{ mb: 1 }}>
					{queryError.message}
				</Typography>
			)}

			<Box sx={{ flex: 1, minHeight: 0 }}>
				<AgGridReact<RecipeStand>
					ref={gridRef}
					rowData={millStands}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					onRowClicked={onRowClicked}
					getRowId={getRowId}
					suppressContextMenu
					suppressColumnVirtualisation
					animateRows={false}
					rowHeight={LIST_ROW_HEIGHT}
					headerHeight={28}
					groupHeaderHeight={28}
					enableBrowserTooltips
					loading={isLoading}
				/>
			</Box>

			<RecipeStandFormModal
				open={formModalOpen}
				initialData={editingStand}
				onSave={handleSave}
				onCancel={() => {
					setFormModalOpen(false);
					setEditingStand(null);
				}}
				isSaving={isUpserting}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
